import {
    all,
    fork,
    take,
    cancelled,
    takeLatest,
    race,
    put,
    call
} from 'redux-saga/effects';

import { eventChannel } from 'redux-saga';

import {
    LOGOUT_USER,
    GET_PROPERTY_MEMBERS,
    RESET_PROPERTY_MEMBERS,
    CHECK_DEVICE_EXISTS
} from '../actions/types';

import { db } from 'config/firebase';

import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';

import {
    getPropertyDevicesSuccess,
    getPropertyDevicesFailure,
    setDeviceExistsValidationResult
} from '../actions/DeviceOperations';

import { errorMessage } from '../../utils/constants';

// Loggers
import { log } from '../../utils/Loggers';
import { calculateDeviceStatus } from '../../utils/devices/DeviceHelper';

const deviceCollectionRef = collection(db, 'devices');

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////// Get Property Devices ////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* propertyDevicesCollectionWatch({ payload }) {
    const { propertyId } = payload;
    let unsubscribePropertyDevicesCollectionData;

    const propertyDevicesCollectionQuery = query(
        deviceCollectionRef,
        where('property_id', '==', propertyId)
    );

    const propertyDevicesCollectionChannel = eventChannel(emit => {
        unsubscribePropertyDevicesCollectionData = onSnapshot(
            propertyDevicesCollectionQuery,
            querySnapshot => {
                const propertyDevices = [];
                querySnapshot.forEach(doc => {
                    const device = doc.data();

                    device.status = calculateDeviceStatus(device);

                    propertyDevices.push(device);
                });
                if (!querySnapshot.size) {
                    emit([]);
                } else {
                    emit(propertyDevices);
                }
            }
        );
        return unsubscribePropertyDevicesCollectionData;
    });

    try {
        while (true) {
            const { userSignOut, resetPropertyMembers, propertyDevicesCollectionData } =
                yield race({
                    userSignOut: take(LOGOUT_USER),
                    resetPropertyMembers: take(RESET_PROPERTY_MEMBERS),
                    propertyDevicesCollectionData: take(propertyDevicesCollectionChannel)
                });

            if (userSignOut || resetPropertyMembers) {
                propertyDevicesCollectionChannel.close();
            } else {
                yield put(getPropertyDevicesSuccess(propertyDevicesCollectionData));
            }
        }
    } catch (error) {
        log('Virtual Guard Error: watching property members by property ID (FS)', {
            error,
            propertyId
        });
        yield put(getPropertyDevicesFailure(error));
    } finally {
        unsubscribePropertyDevicesCollectionData();
        if (yield cancelled()) {
            propertyDevicesCollectionChannel.close();
            unsubscribePropertyDevicesCollectionData();
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////// Check Device Exists /////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* checkDeviceExists({ payload }) {
    const { deviceNumber, facilityCode, ca4kInstance } = payload;

    try {
        const deviceQuery = query(
            collection(db, 'devices'),
            where('device_number', '==', deviceNumber),
            where('facility_code', '==', facilityCode.toString())
        );

        const snapshot = yield call(getDocs, deviceQuery);

        const hasConflict = snapshot.docs.some(doc => {
            const instance = doc.data().ca4k_instance;
            return !ca4kInstance || !instance || instance === ca4kInstance;
        });

        if (!hasConflict) {
            yield put(setDeviceExistsValidationResult(true, null));
        } else {
            yield put(
                setDeviceExistsValidationResult(
                    false,
                    errorMessage.device.device_number.used
                )
            );
        }
    } catch (error) {
        yield put(setDeviceExistsValidationResult(false, errorMessage.validation_failed));
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////// Action Creators For Root Saga ////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* getPropertyDevices() {
    yield takeLatest(GET_PROPERTY_MEMBERS, propertyDevicesCollectionWatch);
}

export function* watchCheckDeviceExists() {
    yield takeLatest(CHECK_DEVICE_EXISTS, checkDeviceExists);
}

export default function* rootSaga() {
    yield all([fork(getPropertyDevices), fork(watchCheckDeviceExists)]);
}
